import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Graph from './graphTable';
import Bar from './bar';
import BarBPM from './barBPM';

// function created for severity
const colorSeverityItem = severety => {
  // condition to define the color as defined in the api
  if (severety == null) {
    return 'gris';
  } else if (severety.toUpperCase() === 'HIGHRISK' || severety.toUpperCase() === 'WORSE') {
    return 'red';
  } else if (severety.toUpperCase() === 'LOWRISK' || severety.toUpperCase() === 'BETTER') {
    return 'green';
  }
  return 'yellow';
};

const colorSeverityText = severety => {
  // condition to define the color as defined in the api
  if (severety == null) {
    return '';
  } else if (severety.toUpperCase() === 'HIGHRISK' || severety.toUpperCase() === 'WORSE') {
    return 'baja respecto al período anterior';
  } else if (severety.toUpperCase() === 'LOWRISK' || severety.toUpperCase() === 'BETTER') {
    return 'mejora respecto al período anterior';
  }
  return '';
};

// function that helps to render the trend of the cells
const renderTrend = (trend, actual, previous, differenceValue, language) => {
  if (trend.toUpperCase() === 'BETTER' && differenceValue > 0) {
    return (
      <span className="green tool">
        <i className="fas fa-arrow-up" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue}% {colorSeverityText(trend.toUpperCase())}
          </h3>
          <p>
            {language.summary.nfa.now} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.risk)}>{actual.value}%</strong>
          </p>
          <p>
            {language.summary.nfa.before} ({previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language.summary.nfa.to} {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.risk : null)}>
              {previous ? `${previous.value}%` : '0%'}
            </strong>
          </p>
        </div>
      </span>
    );
  } else if (trend.toUpperCase() === 'WORSE' && differenceValue > 0) {
    return (
      <span className="red tool">
        <i className="fas fa-arrow-down" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue}% {colorSeverityText(trend.toUpperCase())}
          </h3>
          <p>
            {language.summary.nfa.now} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.risk)}>{actual.value}%</strong>
          </p>
          <p>
            {language.summary.nfa.before} ({previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language.summary.nfa.to} {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.risk : null)}>
              {previous ? `${previous.value}%` : '0%'}
            </strong>
          </p>
        </div>
      </span>
    );
  }
  return <span />;
};

const Table = props => (
  <div className="module-table top">
    <table>
      <thead>
        <tr>
          <th className="th150">
            <a
              role="button"
              data-type="alias"
              tabIndex={0}
              onClick={props.handleSort}
              className="th100"
            >
              {props.language.filter[props.main.toLowerCase()]}{' '}
              <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th className="th100">
            <a
              role="button"
              data-type="ndrivers"
              tabIndex={0}
              onClick={props.handleSort}
              className="th100"
            >
              {props.language.summary.nfa.before.driverNumber} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th className="th100">
            <a role="button" data-type="ninRisk" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.nfa.before.atRisk} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th className="th100">
            <a role="button" data-type="risk" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.nfa.riskPeriod} <i className="fas fa-arrows-alt-v" />
              <div>
                <i className="fas fa-question-circle" />
                <span>{props.language.account.help.code}</span>
              </div>
            </a>
          </th>
          <th className="th250">
            <a role="button" data-type="evolution" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.nfa.driversAtRisk} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th>
            <a role="button" data-type="distribution" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.nfa.riskDistribution} <i className="fas fa-arrows-alt-v" />
              <div>
                <i className="fas fa-question-circle" />
                <span>{props.language.account.help.code}</span>
              </div>
            </a>
          </th>
          <th>
            <a role="button" data-type="severityFails" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.nfa.deviationDitribution}
              <div>
                <i className="fas fa-question-circle" />
                <span>{props.language.account.help.code}</span>
              </div>
            </a>
          </th>
        </tr>
      </thead>
      <tbody>
        {props.datos.map((item, i) => {
          return (
            <tr
              key={`${i}`}
              data-datos={JSON.stringify(item)}
              onClick={props.link}
              className="hover"
            >
              {/* Name */}
              <td className="first">
                <span className={`${colorSeverityItem(item.risk.value.risk)} font16`} />
                {item.alias}
              </td>
              <td className="center">{item.ndrivers}</td>
              <td className="center">{item.ninRisk}</td>
              {/* Average Risk */}
              <td className="center">
                <div className="avg">
                  {
                    <span className={colorSeverityItem(item.risk.value.risk)}>
                      {item.risk.value.value}
                    </span>
                  }
                  {renderTrend(
                    item.risk.trend,
                    item.risk.value,
                    item.risk.previousValue,
                    item.risk.difference,
                    props.language
                  )}

                  <span className="absolute">
                    <p className={colorSeverityItem(item.risk.trend)}>
                      {item.risk.difference !== 0 ? `${item.risk.difference}` : ''}
                    </p>
                  </span>
                </div>
              </td>
              <td>
                <div className="flex align-center">
                  <div className="graph-table">
                    {!props.loadingTable && (
                      <Graph
                        tooltip={props.tooltip}
                        graphColorRange={props.nfa.graphRange}
                        data={item.evolution}
                        id={`graph-${i}`}
                      />
                    )}
                  </div>
                  <div>
                    {item.evolution.length > 0 ? (
                      <p
                        className={colorSeverityItem(
                          item.evolution[item.evolution.length - 1].risk
                        )}
                      >
                        {' '}
                        {item.evolution[item.evolution.length - 1].averageScoreDay}
                      </p>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </td>
              <td>
                {!props.loadingTable ? (
                  <Bar distribution={item.distribution} title={props.title} />
                ) : (
                  <div className="loading-td">
                    <i className="fas fa-spinner fa-pulse" />
                  </div>
                )}
              </td>
              <td>
                {props.BPM[i] && !props.loadingTable ? (
                  <BarBPM
                    distribution={props.BPM[i].response}
                    title={props.title}
                    language={props.language}
                  />
                ) : (
                  <div className="loading-td">
                    <i className="fas fa-spinner fa-pulse" />
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {props.loadingTable && (
      <div className="loading-table">
        <h6>
          <i className="fas fa-spinner fa-spin" />
        </h6>
      </div>
    )}
    {props.pag >= props.count.pages && (
      <div className="loading-table">
        <h6>{props.language.summary.nfa.noData}</h6>
      </div>
    )}
  </div>
);

Table.propTypes = {
  pag: PropTypes.number.isRequired,
  count: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  loadingTable: PropTypes.bool.isRequired,
  language: PropTypes.oneOfType([PropTypes.object]).isRequired,
  datos: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]).isRequired
};

export default Table;



// WEBPACK FOOTER //
// ./source/client/components/summary_nfa/table.jsx